import { Box, Button, Grid, InputLabelProps, Paper, TextField } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import StoryList from '../../components/StoryList';
import { getStoryById } from '../../services/api/Story';
import { getSeasonById } from '../../services/api/Season';
import {
  AB_TEST_TARGET_TYPE,
  IPerformanceCollectionVariant,
  IPerformanceFeaturdCardVariant,
  IPerformanceVariant,
  IStory,
} from '../../services/types';
import StorySelectModal from '../../components/StorySelectModal';
import useStyles from './styles';

interface IStoryVariantProps {
  performanceType: 'story' | 'season';
  testTargetType: AB_TEST_TARGET_TYPE | '';
  variant: IPerformanceVariant;
  onChangeVariantProp: (k: string, v: any) => void;
  onChangeVariantProps: (kv: any) => void;
  removeVariant: () => void;
  editable: boolean;
}

interface IFeaturedVariantProps extends IStoryVariantProps {
  variant: IPerformanceFeaturdCardVariant;
}
interface ICollectionVariantProps extends IStoryVariantProps {
  variant: IPerformanceCollectionVariant;
}

export const PerformanceFeaturedCardVariant = (props: IFeaturedVariantProps): JSX.Element => {
  const classes = useStyles();
  const { editable, performanceType, testTargetType, variant, onChangeVariantProp, onChangeVariantProps, removeVariant } = props;
  const inputLabelProps: InputLabelProps = { shrink: true, classes: { root: classes.inputLabel } };
  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);
  const [selectedStory, setSelectedStory] = useState<IStory>();

  const variantLabel = performanceType === 'story' ? 'Story ID' : 'Season ID';

  useEffect(() => {
    if (!editable) {
      if (variant.data.type === 'season') {
        getSeasonById(variant.data.objectId).then(res => {
          const storyData = res.data.story;
          storyData.seasons = [res.data];
          storyData.seasonId = variant.data.objectId;
          setSelectedStory(storyData);
        });
      } else {
        getStoryById(variant.data.objectId).then(res => {
          setSelectedStory(res.data);
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedStory) {
      return;
    }
    onChangeVariantProp('objectId', performanceType === 'story' ? selectedStory.id : selectedStory.seasonId);
  }, [selectedStory]);

  return (
    <Paper className={classes.variantPaper}>
      <Box paddingBottom={2}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Grid container alignItems="baseline">
            {variant.id && (
              <Grid item xs={12} style={{ marginBottom: 10 }}>
                ID: {variant.id}
              </Grid>
            )}
            <TextField
              label={`${performanceType === 'story' ? 'Story' : 'Season'} Id`}
              value={variant.data.objectId}
              onChange={e => {
                onChangeVariantProp('objectId', Number(e.target.value));
              }}
              disabled={!editable}
              InputLabelProps={inputLabelProps}
              style={{ marginRight: 10 }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setIsStoryModalOpen(true)}
              disabled={!editable}
              style={{ marginLeft: 10 }}
            >
              Select {performanceType === 'story' ? 'Story' : 'Season'}
            </Button>
          </Grid>
          <Button color="secondary" size="small" variant="outlined" onClick={removeVariant} disabled={!editable}>
            <i className="material-icons">delete</i>
          </Button>
        </Box>
        {selectedStory &&
          selectedStory.id === variant.data.objectId &&
          (performanceType === 'story' || selectedStory.seasonId === variant.data.objectId) && (
            <Box>
              <StoryList readonly stories={[selectedStory]} hideSearch />
            </Box>
          )}
      </Box>

      {(testTargetType === AB_TEST_TARGET_TYPE.FEATUREDCARDS || testTargetType === AB_TEST_TARGET_TYPE.CAROUSEL) && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label={'Sub Title'}
              fullWidth
              value={variant.data.subTitle}
              onChange={e => {
                onChangeVariantProp('subTitle', e.target.value);
              }}
              disabled={!editable}
              InputLabelProps={inputLabelProps}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={'Action Text'}
              fullWidth
              value={variant.data.actionText}
              onChange={e => {
                onChangeVariantProp('actionText', e.target.value);
              }}
              disabled={!editable}
              InputLabelProps={inputLabelProps}
            />
          </Grid>
        </Grid>
      )}

      <div style={{ position: 'relative', zIndex: 100 }}>
        <StorySelectModal
          title={performanceType === 'story' ? 'Select Story' : 'Select Season'}
          open={isStoryModalOpen}
          onClick={(story: IStory) => {
            setIsStoryModalOpen(false);
            setSelectedStory(story);
          }}
          onClose={() => setIsStoryModalOpen(false)}
          filter={performanceType}
        />
      </div>
    </Paper>
  );
};

export const PerformanceCollectionVariant = (props: ICollectionVariantProps): JSX.Element => {
  const classes = useStyles();
  const { editable, performanceType, testTargetType, variant, onChangeVariantProp, onChangeVariantProps, removeVariant } = props;
  const inputLabelProps: InputLabelProps = { shrink: true, classes: { root: classes.inputLabel } };
  const [isStoryModalOpen, setIsStoryModalOpen] = useState<boolean>(false);
  const [selectedStory, setSelectedStory] = useState<IStory>();

  const variantLabel = performanceType === 'story' ? 'Story ID' : 'Season ID';

  useEffect(() => {
    if (!editable) {
      if (variant.data.type === 'season') {
        getSeasonById(variant.data.seasonId!).then(res => {
          const storyData = res.data.story;
          storyData.seasons = [res.data];
          storyData.seasonId = variant.data.seasonId;
          setSelectedStory(storyData);
        });
      }
    } else {
      getStoryById(variant.data.storyId).then(res => {
        setSelectedStory(res.data);
      });
    }
  }, []);

  useEffect(() => {
    if (!selectedStory) {
      return;
    }
    if (performanceType === 'story') {
      onChangeVariantProp('storyId', selectedStory.id);
    } else {
      onChangeVariantProps({ storyId: selectedStory?.id, seasonId: selectedStory.seasonId });
    }
  }, [selectedStory]);

  return (
    <Paper className={classes.variantPaper}>
      <Box paddingBottom={2}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Grid container alignItems="baseline">
            {variant.id && (
              <Grid item xs={12} style={{ marginBottom: 10 }}>
                ID: {variant.id}
              </Grid>
            )}
            <TextField
              label={'Story Id'}
              value={variant.data.storyId}
              onChange={e => {
                onChangeVariantProp('storyId', Number(e.target.value));
              }}
              disabled={!editable}
              InputLabelProps={inputLabelProps}
              style={{ marginRight: 10 }}
            />
            {performanceType === 'season' && (
              <TextField
                label={'Season Id'}
                value={variant.data.seasonId}
                onChange={e => {
                  onChangeVariantProp('seasonId', Number(e.target.value));
                }}
                disabled={!editable}
                InputLabelProps={inputLabelProps}
              />
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setIsStoryModalOpen(true)}
              disabled={!editable}
              style={{ marginLeft: 10 }}
            >
              Select {performanceType === 'story' ? 'Story' : 'Season'}
            </Button>
          </Grid>
          <Button color="secondary" size="small" variant="outlined" onClick={removeVariant} disabled={!editable}>
            <i className="material-icons">delete</i>
          </Button>
        </Box>
        {selectedStory &&
          selectedStory.id === variant.data.storyId &&
          (performanceType === 'story' || selectedStory.seasonId === variant.data.seasonId) && (
            <Box>
              <StoryList readonly stories={[selectedStory]} hideSearch />
            </Box>
          )}
      </Box>

      {testTargetType === AB_TEST_TARGET_TYPE.COLLECTION && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={'Story Genre Text'}
              fullWidth
              value={variant.data.storyGenreText}
              onChange={e => {
                onChangeVariantProp('storyGenreText', e.target.value);
              }}
              disabled={!editable}
              InputLabelProps={inputLabelProps}
            />
          </Grid>
        </Grid>
      )}

      <div style={{ position: 'relative', zIndex: 100 }}>
        <StorySelectModal
          title={performanceType === 'story' ? 'Select Story' : 'Select Season'}
          open={isStoryModalOpen}
          onClick={(story: IStory) => {
            setIsStoryModalOpen(false);
            setSelectedStory(story);
          }}
          onClose={() => setIsStoryModalOpen(false)}
          filter={performanceType}
        />
      </div>
    </Paper>
  );
};
